type CleanupFunction = () => void;
type TabInitFunction = () => Promise<CleanupFunction | void>;

export class TabManager {
  private tabButtons: NodeListOf<Element>;
  private tabContents: NodeListOf<Element>;
  private chatInput: HTMLElement | null;
  private cleanupFunctions: Map<string, CleanupFunction>;
  private tabInitializers: Map<string, TabInitFunction>;
  private currentTabId: string | null = null;
  private isInitializing: boolean = false;
  private chatComponent: any = null;  // Store chat component instance

  constructor(
    container: HTMLElement,
    tabInitializers: Map<string, TabInitFunction>
  ) {
    this.tabButtons = container.querySelectorAll('.tab-button');
    this.tabContents = container.querySelectorAll('.tab-content');
    this.chatInput = container.querySelector('#chat-input');
    this.cleanupFunctions = new Map();
    this.tabInitializers = tabInitializers;

    this.initializeTabHandlers();
    
    // Initialize the tab that's marked as active in HTML
    const activeButton = container.querySelector('.tab-button.active');
    if (activeButton) {
      const tabId = activeButton.getAttribute('data-tab');
      if (tabId && !this.currentTabId && !this.isInitializing) {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => this.switchTab(tabId), 0);
      }
    }
  }

  private initializeTabHandlers(): void {
    this.tabButtons.forEach(button => {
      button.addEventListener('click', async () => {
        const tabId = button.getAttribute('data-tab');
        if (tabId) {
          await this.switchTab(tabId);
        }
      });
    });
  }

  public async switchTab(tabId: string): Promise<void> {
    // Prevent switching if we're already on this tab or if we're in the middle of initializing
    if (tabId === this.currentTabId || this.isInitializing) {
      return;
    }

    this.isInitializing = true;

    try {
      // Remove active class from all buttons and contents
      this.tabButtons.forEach(btn => btn.classList.remove('active'));
      this.tabContents.forEach(content => content.classList.remove('active'));
      
      // Add active class to selected button and content
      const selectedButton = document.querySelector(`[data-tab="${tabId}"]`);
      const selectedContent = document.querySelector(`#${tabId}`);
      
      if (selectedButton && selectedContent) {
        selectedButton.classList.add('active');
        selectedContent.classList.add('active');

        // Special handling for chat tab
        if (tabId === 'chat') {
          if (!this.chatComponent) {
            // Initialize chat only if not already initialized
            const initializer = this.tabInitializers.get(tabId);
            if (initializer) {
              const cleanup = await initializer();
              if (cleanup) {
                this.cleanupFunctions.set(tabId, cleanup);
              }
              // Store chat component reference
              const chatContent = document.querySelector('#chat');
              if (chatContent) {
                this.chatComponent = chatContent;
              }
            }
          }
        } else {
          // For non-chat tabs, clean up previous tab if needed
          if (this.currentTabId && this.currentTabId !== 'chat') {
            const cleanup = this.cleanupFunctions.get(this.currentTabId);
            if (cleanup) {
              cleanup();
              this.cleanupFunctions.delete(this.currentTabId);
            }
          }

          // Initialize new tab
          const initializer = this.tabInitializers.get(tabId);
          if (initializer) {
            const cleanup = await initializer();
            if (cleanup) {
              this.cleanupFunctions.set(tabId, cleanup);
            }
          }
        }

        this.currentTabId = tabId;
      }
    } finally {
      this.isInitializing = false;
    }
    
    // Show/hide chat input based on active tab
    if (this.chatInput instanceof HTMLElement) {
      this.chatInput.style.display = tabId === 'chat' ? 'block' : 'none';
    }
  }

  public async initializeDefaultTab(defaultTabId: string = 'chat'): Promise<void> {
    await this.switchTab(defaultTabId);
  }
}
